"use client";

import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";

type ProfileReviewSelectionAction = {
  ariaLabel: string;
  icon: GenericIconType;
  label: string;
  onClick: () => void | Promise<void>;
  tone?: "default" | "danger" | "primary" | "warning";
};

type ProfileReviewSelectionActionsProps = {
  canSelectAll: boolean;
  isBusy?: boolean;
  itemLabelPlural: string;
  itemLabelSingular: string;
  onClearSelection: () => void;
  onSelectAll: () => void;
  primaryAction: ProfileReviewSelectionAction;
  secondaryActions?: ProfileReviewSelectionAction[];
  selectedCount: number;
};

function getActionStyle(action: ProfileReviewSelectionAction) {
  if (action.tone === "primary") {
    return {
      bg: "moveon.primary",
      borderColor: undefined,
      color: "white",
      variant: undefined,
    } as const;
  }

  if (action.tone === "danger") {
    return {
      bg: undefined,
      borderColor: "moveon.dangerBorder",
      color: "moveon.danger",
      variant: "outline",
    } as const;
  }

  if (action.tone === "warning") {
    return {
      bg: undefined,
      borderColor: undefined,
      color: "moveon.warning",
      variant: "ghost",
    } as const;
  }

  return {
    bg: undefined,
    borderColor: undefined,
    color: undefined,
    variant: "outline",
  } as const;
}

function SelectionActionButton({
  action,
  disabled,
}: {
  action: ProfileReviewSelectionAction;
  disabled: boolean;
}) {
  const style = getActionStyle(action);

  return (
    <Button
      type="button"
      size="sm"
      variant={style.variant}
      bg={style.bg}
      color={style.color}
      borderColor={style.borderColor}
      disabled={disabled}
      onClick={() => {
        void action.onClick();
      }}
      aria-label={action.ariaLabel}
    >
      <GenericIcon icon={action.icon} />
      <Text as="span" display={{ base: "none", sm: "inline" }}>
        {action.label}
      </Text>
    </Button>
  );
}

function SelectAllButton({
  disabled,
  itemLabelPlural,
  onSelectAll,
}: {
  disabled: boolean;
  itemLabelPlural: string;
  onSelectAll: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={disabled}
      onClick={onSelectAll}
      aria-label={`Select all ${itemLabelPlural}`}
    >
      <GenericIcon icon="select" />
      <Text as="span" display={{ base: "none", sm: "inline" }}>
        Select all
      </Text>
    </Button>
  );
}

function ClearSelectionButton({
  disabled,
  itemLabelPlural,
  onClearSelection,
}: {
  disabled: boolean;
  itemLabelPlural: string;
  onClearSelection: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      color="moveon.warning"
      disabled={disabled}
      onClick={onClearSelection}
      aria-label={`Clear selected ${itemLabelPlural}`}
    >
      <GenericIcon icon="unselect" />
      <Text as="span" display={{ base: "none", sm: "inline" }}>
        Clear
      </Text>
    </Button>
  );
}

const SELECTION_ACTIONS_SHELL_PROPS = {
  align: "center",
  bg: "moveon.surface.elevatedBg",
  borderColor: "moveon.surface.border",
  borderRadius: "md",
  borderWidth: "1",
  gap: "3",
  justify: "space-between",
  px: "3",
  py: "2",
  width: "full",
  wrap: "wrap",
} as const;

export default function ProfileReviewSelectionActions({
  canSelectAll,
  isBusy = false,
  itemLabelPlural,
  itemLabelSingular,
  onClearSelection,
  onSelectAll,
  primaryAction,
  secondaryActions = [],
  selectedCount,
}: ProfileReviewSelectionActionsProps) {
  if (selectedCount === 0) {
    if (!canSelectAll) {
      return null;
    }

    return (
      <Flex {...SELECTION_ACTIONS_SHELL_PROPS}>
        <SelectAllButton
          disabled={isBusy}
          itemLabelPlural={itemLabelPlural}
          onSelectAll={onSelectAll}
        />
        <Box aria-hidden="true" flex="1" />
      </Flex>
    );
  }

  return (
    <Flex {...SELECTION_ACTIONS_SHELL_PROPS}>
      <HStack gap="2.5" align="center" minW="0" flex="1">
        <ClearSelectionButton
          disabled={isBusy}
          itemLabelPlural={itemLabelPlural}
          onClearSelection={onClearSelection}
        />
        <Box minW={0}>
          <Text
            fontSize="xs"
            color="moveon.text.muted"
            textTransform="uppercase"
          >
            Selection
          </Text>
          <Text fontSize="sm" fontWeight="semibold">
            {selectedCount}{" "}
            {selectedCount === 1 ? itemLabelSingular : itemLabelPlural}
          </Text>
        </Box>
      </HStack>

      <HStack gap="2" align="center" justify="flex-end" flexWrap="wrap">
        {secondaryActions.map((action) => (
          <SelectionActionButton
            key={action.ariaLabel}
            action={action}
            disabled={isBusy}
          />
        ))}
        <SelectionActionButton action={primaryAction} disabled={isBusy} />
      </HStack>
    </Flex>
  );
}
